import React, { memo, useState } from 'react';
import AddBoxIcon from '@mui/icons-material/AddBox';
import AirplayIcon from '@mui/icons-material/Airplay';
import { Box, ClickAwayListener, Fade, Link, List, ListItem, ListItemIcon, ListItemText, Popper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { formatDuration, formatTimeDuration } from 'utils/format';
import EdgedButton from 'components/common/EdgedButton';
import IconCalendar from 'components/svg/icon-calendar.svg';
import IconClock from 'components/svg/icon-clock.svg';
import IconPin from 'components/svg/icon-pin.svg';
import AddToCalendarMenu from './addToCalendarMenu/AddToCalendarMenu';
import { getSessionsGroups } from '../../utils/transform';
import useCustomOrgShowTime from '../../hooks/useCustomOrgShowTime';

interface EventSummaryProps {
  event: any;
  color?: 'primary' | 'secondary';
  onLocationClick?: () => void;
  calendar?: boolean;
  location?: boolean;
}

const useStyles = makeStyles()((theme) => ({
  timezone: {
    color: theme.palette.text.primary,
  },
}));

const useListItemIconStyles = makeStyles()((theme) => ({
  root: {
    marginTop: 0,
    marginBottom: 2,
    marginLeft: 2,
    minWidth: theme.spacing(3),
    '.primary &': {
      color: theme.palette.primary.main,
    },
    '.secondary &': {
      color: theme.palette.primary.light,
    },
  },
}));

const useListItemTextStyles = makeStyles()((theme) => ({
  primary: {
    fontWeight: theme.typography.fontWeightMediumBold,
    // textTransform: 'uppercase',
    '.primary &': {
      color: theme.palette.text.primary,
    },
    '.secondary &': {
      color: theme.palette.custom.white,
    },
  },
  multiline: {
    margin: 0,
    marginLeft: 2,
  },
}));

const useLocationLinkStyles = makeStyles()((theme) => ({
  root: {
    '.primary &': {
      color: theme.palette.primary.main,
    },
    '.secondary &': {
      color: theme.palette.primary.light,
    },
  },
}));

const useCalendarButtonStyles = makeStyles()((theme) => ({
  root: {
    padding: '4px 8px',
    margin: -8,
    '.primary &': {
      color: theme.palette.text.primary,
    },
    '.secondary &': {
      color: theme.palette.primary.main,
    },
    '& .MuiButton-startIcon': {
      marginLeft: 0,
    },
  },
}));

const EventSummary: React.FC<React.PropsWithChildren<EventSummaryProps>> = ({
  color = 'primary',
  onLocationClick,
  calendar,
  location,
  event,
}) => {
  const { classes } = useStyles();
  const { classes: listItemIconClasses } = useListItemIconStyles();
  const { classes: listItemTextClasses } = useListItemTextStyles();
  const { classes: locationLinkClasses } = useLocationLinkStyles();
  const { classes: calendarButtonClasses } = useCalendarButtonStyles();
  const [t] = useTranslation('public');
  const [open, setOpen] = useState(false);
  const [calendarEl, setCalendarEl] = React.useState<null | HTMLElement>(null);

  const handleCalendarClick = (event: React.MouseEvent<HTMLElement>) => {
    setCalendarEl(event.currentTarget);
    setOpen(!open);
  };

  const {
    start_at,
    end_at,
    timezone,
    address,
    is_virtual,
    has_location,
    sessions,
    hide_date,
    hide_time,
    livestream_link,
    organization,
  } = event;

  const time = start_at && end_at && formatTimeDuration(start_at, end_at, timezone);
  const duration = start_at && end_at && formatDuration(start_at, end_at, timezone);
  const hasOnLocationClickEvent = !!onLocationClick;
  const { customShowTime } = useCustomOrgShowTime(organization.id);

  const timesLabel = () => {
    if (Array.isArray(sessions) && sessions.length > 1) {
      return getSessionsGroups(sessions, timezone).length > 1
        ? t('common:general.multipleDays')
        : t('common:general.multipleTimes');
    }
    return (
      <>
        {time.split('|')[0]} <span className={classes.timezone}>{time.split('|')[1]}</span>
      </>
    );
  };

  return (
    <List className={color} dense disablePadding>
      {!hide_date && (
        <ListItem disableGutters>
          <ListItemIcon classes={listItemIconClasses}>
            <IconCalendar />
          </ListItemIcon>
          <ListItemText classes={listItemTextClasses}>{duration}</ListItemText>
        </ListItem>
      )}
      {!hide_time && customShowTime && time && (
        <ListItem disableGutters>
          <ListItemIcon style={{ marginTop: '2px' }} classes={listItemIconClasses}>
            <IconClock />
          </ListItemIcon>
          <ListItemText classes={listItemTextClasses}>{timesLabel()}</ListItemText>
        </ListItem>
      )}
      {calendar && (
        <>
          <ListItem disableGutters>
            <ListItemText classes={listItemTextClasses}>
              <EdgedButton
                variant="text"
                size="small"
                edge="end"
                classes={calendarButtonClasses}
                startIcon={<AddBoxIcon color="primary" />}
                onClick={handleCalendarClick}
              >
                {t<string>('event.eventSummary.toCalendar')}
              </EdgedButton>
            </ListItemText>
          </ListItem>
          <Popper sx={{ zIndex: 100, height: 166 }} id={event.id} open={open} anchorEl={calendarEl} placement="top">
            <Box sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <Fade in={open}>
                  <div>
                    <AddToCalendarMenu event={event} open={open} />
                  </div>
                </Fade>
              </ClickAwayListener>
            </Box>
          </Popper>
        </>
      )}
      {(livestream_link || is_virtual) && (
        <ListItem disableGutters>
          <ListItemIcon classes={listItemIconClasses}>
            <AirplayIcon color="inherit" fontSize="small" />
          </ListItemIcon>
          <ListItemText classes={listItemTextClasses}>{t<string>('event.eventSummary.onlineEvent')}</ListItemText>
        </ListItem>
      )}
      {location && has_location && (
        <ListItem disableGutters alignItems="flex-start">
          <ListItemIcon classes={listItemIconClasses} style={{ padding: '6px 0px' }}>
            <IconPin />
          </ListItemIcon>
          <ListItemText
            classes={listItemTextClasses}
            style={{ marginLeft: 0, padding: '6px 0px' }}
            primary={address?.address_name}
            secondary={
              <Link
                classes={locationLinkClasses}
                component={hasOnLocationClickEvent ? 'button' : 'p'}
                color="inherit"
                align="left"
                onClick={onLocationClick}
              >
                <>
                  {address?.address1} <br />
                  {address?.address2}
                  {address?.address2 && <br />}
                  {address?.city}, {address?.state}, {address?.postal_code}
                </>
              </Link>
            }
          />
        </ListItem>
      )}
    </List>
  );
};

export default memo(EventSummary);
